import { Content, ContentAudience } from '@amzn/awscat-pattern-library-service-model';

import { resetOpenSourceCodeReview } from '../api/ContentUtil';
import { validateCodeRepoAudience, validateCodeReview } from '../api/ValidationUtil';

/** Why an audience change was rejected. */
export type AudienceChangeErrorCode =
  /** Code-repo URL doesn't match the target audience. */
  | 'codeRepoAudience'
  /** AppSec/OSS code review hasn't cleared for the target audience. */
  | 'codeReview';

/** A single reason an audience change was rejected. */
export interface AudienceChangeError {
  readonly code: AudienceChangeErrorCode;
  readonly message: string;
}

/**
 * Outcome of an audience switch. `kind: 'ok'` means the caller should stage
 * `content` together with `audience`; `kind: 'rejected'` means the caller
 * should surface `errors` and not stage.
 */
export type AudienceChangeResult =
  | { readonly kind: 'ok'; readonly content: Content; readonly audience: ContentAudience }
  | { readonly kind: 'rejected'; readonly errors: readonly AudienceChangeError[] };

/** One audience tile, with whether the user is allowed to pick it. */
export interface AudienceOption {
  /** The audience this tile represents. */
  readonly value: ContentAudience;
  /** True when the tile should be disabled (lower than a previously-published audience). */
  readonly isDisabled: boolean;
}

/**
 * Audiences that the metadata tab renders as tiles. Matches legacy: only
 * `INTERNAL` and `PUBLIC` are user-facing; `EXTERNAL` and `PARTNER` exist
 * in the model but are not directly selectable.
 */
const RENDERED_AUDIENCES: readonly ContentAudience[] = [
  ContentAudience.INTERNAL,
  ContentAudience.PUBLIC,
];

/**
 * Audience ordering used to decide which tiles to disable after publication.
 * `EXTERNAL` is included so a document previously published at the EXTERNAL
 * level still locks the (lower) `INTERNAL` tile, even though `EXTERNAL`
 * itself isn't rendered. Invariant: every `RENDERED_AUDIENCES` value is
 * also in `AUDIENCE_RANKING`.
 */
const AUDIENCE_RANKING: readonly ContentAudience[] = [
  ContentAudience.INTERNAL,
  ContentAudience.EXTERNAL,
  ContentAudience.PUBLIC,
];

/**
 * Computes the audience tiles to render along with each tile's disabled
 * state. Once a document has been published, only upgrades are allowed: a
 * rendered audience is disabled when its rank is lower than at least one
 * previously-published audience. Pass an empty array for
 * `previouslyPublishedAudiences` to allow every tile.
 */
export function getAllowedAudienceOptions(
  previouslyPublishedAudiences: readonly ContentAudience[],
): readonly AudienceOption[] {
  return RENDERED_AUDIENCES.map((value) => {
    const valueRank = AUDIENCE_RANKING.indexOf(value);
    return {
      value,
      // A value not in the ranking (-1) is never disabled; a past audience
      // not in the ranking (-1) never disables anything.
      isDisabled: previouslyPublishedAudiences.some((past) => {
        const pastRank = AUDIENCE_RANKING.indexOf(past);
        return valueRank >= 0 && pastRank >= 0 && valueRank < pastRank;
      }),
    };
  });
}

/**
 * Computes the content + audience to stage for an audience switch, or the
 * validation errors that block it. Pure: takes the previous state and the
 * requested new audience, returns the next state OR the rejection. The
 * caller (the metadata view model) decides what to do next.
 */
export function applyAudienceChange(
  previousContent: Content,
  previousAudience: ContentAudience,
  nextAudience: ContentAudience,
): AudienceChangeResult {
  // Validate against the original content (before OSS reset) — legacy validates
  // code-review status while the OSS fields are still present, so an approved
  // review passes even though the reset will clear those fields afterward.
  const errors: AudienceChangeError[] = [];
  const codeRepoMessage = validateCodeRepoAudience(nextAudience, previousContent);
  if (codeRepoMessage) {
    errors.push({ code: 'codeRepoAudience', message: codeRepoMessage });
  }
  const codeReviewMessage = validateCodeReview(nextAudience, previousContent);
  if (codeReviewMessage) {
    errors.push({ code: 'codeReview', message: codeReviewMessage });
  }

  if (errors.length > 0) {
    return { kind: 'rejected', errors };
  }

  // Clear OSS review fields when switching out of internal with code links present;
  // `resetOpenSourceCodeReview` keys off `previousAudience`.
  const contentAfterOssReset = resetOpenSourceCodeReview(previousAudience, previousContent);
  return { kind: 'ok', content: contentAfterOssReset, audience: nextAudience };
}
